import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useBoundStore } from "../../store";
import { useI18n } from "../../i18n";

interface CreateRuleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  playbookId: string;
}

export default function CreateRuleDialog({ open, onOpenChange, playbookId }: CreateRuleDialogProps) {
  const { t } = useI18n();
  const createRule = useBoundStore((state) => state.createRule);
  const isLoading = useBoundStore((state) => state.isLoading);

  const [name, setName] = useState("");
  const [instructions, setInstructions] = useState("");
  const [comments, setComments] = useState("");
  const [enableComments, setEnableComments] = useState(true);
  const [isActive, setIsActive] = useState(true);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim() || !instructions.trim()) {
      toast.error(t.playbooks.createRuleDialog.requiredFields);
      return;
    }

    const rule = await createRule(playbookId, {
      name: name.trim(),
      instructions: instructions.trim(),
      comments: comments.trim() || undefined,
      enableComments,
      isActive,
    });

    if (rule) {
      toast.success(t.playbooks.createRuleDialog.createSuccess);
      // Reset form
      setName("");
      setInstructions("");
      setComments("");
      setEnableComments(true);
      setIsActive(true);
      onOpenChange(false);
    } else {
      toast.error(t.playbooks.createRuleDialog.createFailed);
    }
  };

  const handleCancel = () => {
    setName("");
    setInstructions("");
    setComments("");
    setEnableComments(true);
    setIsActive(true);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{t.playbooks.createRuleDialog.title}</DialogTitle>
            <DialogDescription>{t.playbooks.createRuleDialog.description}</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">
                {t.playbooks.createRuleDialog.nameLabel} <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t.playbooks.createRuleDialog.namePlaceholder}
                required
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="instructions">
                {t.playbooks.createRuleDialog.instructionsLabel} <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="instructions"
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder={t.playbooks.createRuleDialog.instructionsPlaceholder}
                rows={4}
                required
              />
            </div>

            <div className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <Label htmlFor="enableComments">{t.playbooks.createRuleDialog.enableCommentsLabel}</Label>
                <p className="text-sm text-muted-foreground">
                  {t.playbooks.createRuleDialog.enableCommentsHint}
                </p>
              </div>
              <Switch id="enableComments" checked={enableComments} onCheckedChange={setEnableComments} />
            </div>

            {enableComments && (
              <div className="grid gap-2">
                <Label htmlFor="comments">{t.playbooks.createRuleDialog.commentGuidelinesLabel}</Label>
                <Textarea
                  id="comments"
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  placeholder={t.playbooks.createRuleDialog.commentGuidelinesPlaceholder}
                  rows={3}
                />
              </div>
            )}

            <div className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <Label htmlFor="isActive">{t.playbooks.createRuleDialog.activeLabel}</Label>
                <p className="text-sm text-muted-foreground">
                  {t.playbooks.createRuleDialog.activeHint}
                </p>
              </div>
              <Switch id="isActive" checked={isActive} onCheckedChange={setIsActive} />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleCancel} disabled={isLoading}>
              {t.playbooks.createRuleDialog.cancel}
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? t.playbooks.createRuleDialog.creating : t.playbooks.createRuleDialog.submit}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
